import React, { useState, useEffect } from 'react';
import { Plus, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { Button, IconButton, Spinner, Toggle } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import StageCard from './StageCard';
import StageForm from './StageForm';
import StageStatusesModal from './StageStatusesModal';
import { stages as stageService } from '../../services/apiService';
import { formatApiError } from '../../utils/formatApiError';

/**
 * StageList
 *
 * Проще, чем StatusList: стадий немного (весь жизненный цикл — единицы, не
 * сотни), поэтому без пагинации, поиска и группировки — плоский список,
 * отсортированный по stage_order.
 *
 * Порядок — drag-and-drop (как в Dropbox/Trello), не ручной ввод числа:
 * перетащил карточку — весь список тут же перенумеровался 1..N и ушёл на
 * сервер. Ручное поле "Order" в форме убрано (см. StageForm.jsx) — искусственные
 * зазоры (10, 20, 30...) были нужны только под ручной ввод "куда-то между",
 * а раз список сам перенумеровывается при каждом перетаскивании, зазоры не нужны.
 */
const StageList = () => {
  const [stagesData, setStagesData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [editingStageId, setEditingStageId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [statusesModalStage, setStatusesModalStage] = useState(null);
  // Удалённые стадии не участвуют в порядке и перетаскивании — держим их
  // отдельно и показываем только по переключателю
  const [deletedStages, setDeletedStages] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const loadStages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await stageService.getStages({ active_only: 'false', with_counts: 'true' });
      const list = Array.isArray(response?.data) ? response.data : [];
      const sorted = [...list].sort((a, b) => (a.stage_order || 0) - (b.stage_order || 0));
      // is_active = false — стадия удалена (мягкое удаление)
      setStagesData(sorted.filter((stage) => stage.is_active));
      setDeletedStages(sorted.filter((stage) => !stage.is_active));
    } catch (err) {
      console.error('Error loading stages:', err);
      setError(formatApiError(err, 'Error loading stages'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStages();
  }, []);

  const handleCreate = async (formData) => {
    try {
      await stageService.createStage(formData);
      setIsCreating(false);
      loadStages();
    } catch (error) {
      toast.error(formatApiError(error, 'Error creating stage'));
      throw error;
    }
  };

  const handleUpdate = async (id, updateData) => {
    try {
      await stageService.updateStage(id, updateData);
      setEditingStageId(null);
      loadStages();
    } catch (error) {
      throw error;
    }
  };

  const handleDelete = async (id) => {
    await stageService.deleteStage(id);
    loadStages();
  };

  const handleRestore = async (id) => {
    await stageService.restoreStage(id);
    loadStages();
  };

  // Переставляет стадию с fromIndex на toIndex, перенумеровывает 1..N и сразу
  // сохраняет — никакого отдельного "Save": перетащил/нажал стрелку и готово,
  // как в Dropbox. При ошибке откатывает список к порядку до перестановки.
  const moveStage = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= stagesData.length) return;

    const previous = stagesData;
    const reordered = [...stagesData];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const renumbered = reordered.map((stage, index) => ({ ...stage, stage_order: index + 1 }));

    setStagesData(renumbered);
    setReordering(true);
    try {
      await stageService.updateStageOrder(
        renumbered.map((stage) => ({ id: stage.id, stage_order: stage.stage_order }))
      );
    } catch (err) {
      console.error('Error saving stage order:', err);
      toast.error(formatApiError(err, 'Error saving stage order'));
      setStagesData(previous);
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Stage Management</h2>
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
            Where a talent is in the pipeline — independent of Status. Drag a card to reorder.
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-4 sm:w-auto">
          <Toggle
            checked={showDeleted}
            onChange={setShowDeleted}
            label={`Show deleted (${deletedStages.length})`}
            color="primary"
            size="small"
          />
          <Button color="primary" onClick={() => setIsCreating(true)} sx="whitespace-nowrap">
            <Plus className="w-4 h-4 flex-shrink-0" />
            New Stage
          </Button>
        </div>
      </div>

      {isCreating && (
        <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-md border border-light-border-primary dark:border-dark-border-primary p-6">
          <StageForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreating(false)}
            submitLabel="Create"
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <Spinner size="large" color="primary" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 dark:bg-red-900/30 dark:border-red-800">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Loading Error</h3>
            <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
            <Button color="error" size="small" onClick={loadStages} sx="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      ) : stagesData.length === 0 ? (
        <div className="text-center py-12 bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-light-border-primary dark:border-dark-border-primary">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-2">No stages found</h3>
          <p className="text-light-text-tertiary dark:text-dark-text-tertiary">Create the first stage to get started</p>
        </div>
      ) : (
        // Один столбец, а не сетка карточками: у drag-and-drop в многоколоночной
        // сетке неоднозначная семантика ("сосед справа" — это следующий по
        // порядку или на строку ниже?), в линейном списке — нет
        <div className="space-y-1.5">
          {stagesData.map((stage, index) =>
            editingStageId === stage.id ? (
              <StageCard
                key={stage.id}
                stage={stage}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onManageStatuses={setStatusesModalStage}
                isEditing
                onEdit={setEditingStageId}
                onCancelEdit={() => setEditingStageId(null)}
              />
            ) : (
              <div
                key={stage.id}
                draggable={!reordering}
                onDragStart={(e) => e.dataTransfer.setData('text/plain', String(index))}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
                  if (!isNaN(fromIndex)) moveStage(fromIndex, index);
                }}
                className={`flex min-w-0 flex-col items-stretch gap-1 sm:flex-row sm:items-center ${reordering ? 'opacity-60 pointer-events-none' : ''}`}
              >
                <div className="flex items-center gap-0.5 text-light-text-tertiary dark:text-dark-text-tertiary cursor-move flex-shrink-0" title="Drag to reorder">
                  <GripVertical className="w-3.5 h-3.5" />
                  <IconButton
                    variant="ghost"
                    color="neutral"
                    size="medium"
                    aria-label="Move up"
                    title="Move up"
                    disabled={index === 0}
                    onClick={() => moveStage(index, index - 1)}
                    icon={<ArrowUp className="w-3.5 h-3.5" />}
                  />
                  <IconButton
                    variant="ghost"
                    color="neutral"
                    size="medium"
                    aria-label="Move down"
                    title="Move down"
                    disabled={index === stagesData.length - 1}
                    onClick={() => moveStage(index, index + 1)}
                    icon={<ArrowDown className="w-3.5 h-3.5" />}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <StageCard
                    stage={stage}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    onManageStatuses={setStatusesModalStage}
                    isEditing={false}
                    onEdit={setEditingStageId}
                    onCancelEdit={() => setEditingStageId(null)}
                  />
                </div>
              </div>
            )
          )}
        </div>
      )}

      {!loading && !error && showDeleted && (
        <div className="space-y-1.5">
          <h3 className="text-sm font-semibold text-light-text-secondary dark:text-dark-text-secondary">
            Deleted stages
          </h3>
          {deletedStages.length === 0 ? (
            <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No deleted stages</p>
          ) : (
            deletedStages.map((stage) => (
              <StageCard key={stage.id} stage={stage} onRestore={handleRestore} />
            ))
          )}
        </div>
      )}

      <StageStatusesModal
        isOpen={!!statusesModalStage}
        onClose={() => setStatusesModalStage(null)}
        stage={statusesModalStage}
        onSaved={loadStages}
      />
    </div>
  );
};

export default StageList;
